/**
 * READERS ON THIS WORKSTATION — compact list on a SUNMI register's Terminal
 * (EMV/SoftPOS) tab: only the primary and backup THIS register uses, with their
 * keys and status, Edit, and Use as primary / backup (a swap). The rest of the
 * location's pool is never listed here — this page sets up one workstation
 * (owner 2026-10-29); another device is chosen in the Primary/Backup pickers,
 * which list the location's pool and the other locations' readers.
 */
import React from 'react';
import { Pencil } from 'lucide-react';
import { Pill } from '../payment-types/ui';
import { deviceTypeLabel, isSpinDevice } from './deviceForm';
import { type CardSetup, type CardSetupPolicy, type Role, readerKindOf, allowedKinds, readerId, masked } from './cardSetupModel';

interface Props {
  registerId: string;
  devices: any[];
  draft: CardSetup;
  policy: CardSetupPolicy;
  status: Record<string, any>;
  disabled?: boolean;
  onEdit: (device: any) => void;
  onUse: (role: Role, deviceId: string) => void;
}

const statusOf = (d: any, st: any) => {
  if (d?.isActive === false || d?.status === 'deactivated' || d?.status === 'inactive') return <Pill tone="off">Inactive</Pill>;
  if (String(d?.deviceClass) === 'dejavoo_rubytill_pay') {
    if (!st) return <Pill>—</Pill>;
    return <Pill tone={st.status?.online ? 'ok' : st.status?.paired ? 'warn' : 'off'}>{st.status?.online ? 'Online' : st.status?.paired ? 'Offline' : 'Not paired'}</Pill>;
  }
  return <Pill>{d?.status ? String(d.status).replace(/_/g, ' ') : 'Registered'}</Pill>;
};

const keysOf = (d: any, st: any) => {
  if (isSpinDevice(d)) {
    const cloud = String(d.deviceClass) === 'dejavoo_spin_cloud';
    return (
      <span className="flex flex-wrap gap-1">
        {!d.spinRegisterId && <Pill tone="warn">No Register Id</Pill>}
        {d.spinAuthKeyMasked ? <Pill tone="ok">Auth Key ✓</Pill> : cloud ? <Pill tone="warn">No Auth Key</Pill> : null}
        {d.hasOwnIposKeys ? <Pill tone="ok">iPOS own ✓</Pill> : <Pill>iPOS platform</Pill>}
      </span>
    );
  }
  if (String(d?.deviceClass) === 'dejavoo_rubytill_pay') {
    return st?.rtpay?.merchantCodeLast4 ? <Pill tone="ok">Merchant code ✓</Pill> : <Pill>—</Pill>;
  }
  return null;
};

export const DeviceTable: React.FC<Props> = ({ registerId, devices, draft, policy, status, disabled, onEdit, onUse }) => {
  const roleOf = (id: string) => (readerId(draft, 'primary') === id ? 'Primary' : readerId(draft, 'backup') === id ? 'Backup' : '—');
  const canUse = (d: any, role: Role) => {
    const k = readerKindOf(d);
    if (!k || !allowedKinds(policy, role).includes(k)) return false;
    if (d.isActive === false || d.status === 'deactivated' || d.status === 'inactive') return false;
    if (d.registerId && d.registerId !== registerId) return false;
    return readerId(draft, role) !== d.deviceId;
  };
  const rank = (id: string) => (readerId(draft, 'primary') === id ? 0 : 1);
  const mine = devices.filter((d) => roleOf(d.deviceId) !== '—').sort((a, b) => rank(a.deviceId) - rank(b.deviceId));
  const btn = 'min-h-[44px] rounded-lg px-2 text-xs font-semibold text-ruby hover:bg-[#FFF1F3] disabled:cursor-not-allowed disabled:text-stone-300 disabled:hover:bg-transparent';

  return (
    <div className="border-t border-stone-200" data-testid="cs-device-table">
      <div className="px-[18px] pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-stone-500">Readers on this workstation</div>
      <div className="overflow-x-auto px-[18px] pb-3">
        <table className="w-full min-w-[760px] text-[12.5px]">
          <thead className="text-left text-[10.5px] font-semibold uppercase tracking-wide text-stone-400">
            <tr>
              <th className="py-1.5 pr-2">Name</th><th className="pr-2">Type</th><th className="pr-2">Serial</th><th className="pr-2">TPN</th>
              <th className="pr-2">Keys</th><th className="pr-2">Status</th><th className="pr-2">Role</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mine.map((d) => {
              const st = status[d.deviceId];
              const bound = d.registerId && d.registerId !== registerId;
              return (
                <tr key={d.deviceId} data-testid={`cs-dev-${d.deviceId}`} className="border-t border-stone-100 align-middle">
                  <td className="py-1 pr-2 font-medium text-stone-900">{d.deviceName || '—'}</td>
                  <td className="pr-2 text-stone-700">{deviceTypeLabel(d)}</td>
                  <td className="pr-2 font-mono text-stone-700">{d.serialNumber || '—'}</td>
                  <td className="pr-2 font-mono text-stone-700">{d.tpnMasked || (d.tpn ? masked(d.tpn) : '—')}</td>
                  <td className="pr-2">{keysOf(d, st)}</td>
                  <td className="pr-2">{statusOf(d, st)}{bound && <span className="ml-1"><Pill tone="warn">On another till</Pill></span>}</td>
                  <td className="pr-2 font-semibold text-stone-800">{roleOf(d.deviceId)}</td>
                  <td className="whitespace-nowrap text-right">
                    <button type="button" className={btn} disabled={disabled} onClick={() => onEdit(d)} aria-label={`Edit ${d.deviceName || 'device'}`}>
                      <Pencil size={13} className="mr-1 inline" />Edit
                    </button>
                    <button type="button" className={btn} disabled={disabled || !canUse(d, 'primary')} onClick={() => onUse('primary', d.deviceId)}>Use as primary</button>
                    <button type="button" className={btn} disabled={disabled || !canUse(d, 'backup')} onClick={() => onUse('backup', d.deviceId)}>Use as backup</button>
                  </td>
                </tr>
              );
            })}
            {!mine.length && (
              <tr><td colSpan={8} className="py-3 text-stone-500">No reader on this workstation yet — pick a primary above, or Register payment device.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DeviceTable;
